import { getDatasetControlMethodActionApiRequestData } from '../../../../api/utils/requestData/getDatasetControlMethodActionApiRequestData';
import { getSubsetControlMethodActionApiRequestData } from '../../../../api/utils/requestData/getSubsetControlMethodActionApiRequestData';
import { Dataset, DatasetControl } from '../../../../types/types';
import { ResponseActionHandler } from '../../../../types/context';
import { getDataKey } from '../../../../utils/getDataKey';
import { createGetAllIdsFunction } from '../../../../utils/json/customFunctions/createGetAllIdsFunction/createGetAllIdsFunction';
import useApiErrorHandling from '../../../useApiErrorHandling';
import { useControlActionModalContent } from '../../../useControlActionModalContent';
import { displayConfirmationModal } from '../displayConfirmationModal';
import { getMethodApiCallback } from '../getMethodApiCallback';
import { getResolvedRowsToUse } from '../getResolvedRowsToUse';

export const getMethodTypeCallback = (
  dataset: Dataset,
  rootDataset: Dataset | undefined,
  rowsToUse: any[],
  currentRow: any,
  masterExpandedRow: any | undefined,
  selectedVirtualRows: number,
  deselectedRowIds: Set<string>,
  control: DatasetControl,
  token: string | null,
  cachedQueryId: string,
  isSlaveDataset: boolean,
  refetchData: () => void,
  fetchDatasetData: () => Promise<any[]>,
  openControlModal: ReturnType<typeof useControlActionModalContent>['open'],
  closeControlModal: ReturnType<typeof useControlActionModalContent>['close'],
  setModalLoading: ReturnType<
    typeof useControlActionModalContent
  >['setModalLoading'],
  handleApiError: ReturnType<typeof useApiErrorHandling>,
  getResponseActionHandler?: (name: string) => ResponseActionHandler | undefined
) => {
  return async () => {
    try {
      const shouldDisplayConfirmationModal = displayConfirmationModal(
        control,
        rowsToUse
      );

      if (shouldDisplayConfirmationModal) {
        setModalLoading(true);
      }

      // Single-flight the all-rows prefetch so virtual-row resolution and
      // $getAllIds() share one request per action click.
      let allRowsPrefetch: ReturnType<typeof fetchDatasetData> | undefined;
      const fetchAllRows = (): ReturnType<typeof fetchDatasetData> =>
        (allRowsPrefetch = allRowsPrefetch ?? fetchDatasetData());

      const resolvedRowsToUse = await getResolvedRowsToUse(
        dataset,
        rowsToUse,
        selectedVirtualRows,
        fetchAllRows,
        deselectedRowIds
      );

      const proceed = async () => {
        // Set only when actually held — null drops the x-cachedquery-id header
        const requestData = isSlaveDataset
          ? getSubsetControlMethodActionApiRequestData(
              dataset,
              rootDataset as Dataset,
              control,
              token,
              masterExpandedRow,
              currentRow,
              resolvedRowsToUse,
              cachedQueryId || null
            )
          : getDatasetControlMethodActionApiRequestData(
              dataset,
              control,
              token,
              currentRow,
              resolvedRowsToUse,
              cachedQueryId || null
            );

        // $getAllIds() fetches every filtered row on demand — built per click so
        // its in-flight cache is fresh for each action invocation.
        const getAllIds = createGetAllIdsFunction(
          fetchAllRows,
          getDataKey(dataset)
        );

        const callback = getMethodApiCallback(
          control,
          token,
          requestData,
          refetchData,
          handleApiError,
          getResponseActionHandler,
          { getAllIds }
        );

        if (shouldDisplayConfirmationModal) {
          setModalLoading(true);
        }

        await callback();

        closeControlModal();
        setModalLoading(false);
      };

      if (shouldDisplayConfirmationModal) {
        setModalLoading(false);
        return openControlModal(dataset, control, resolvedRowsToUse, proceed);
      }

      proceed();
    } catch (error) {
      setModalLoading(false);
      throw error;
    }
  };
};
